import React, {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import phog from '../utils/analytics/posthog/posthog';
import {
  isPostHogSlug,
  POSTHOG_AB_PREFIX,
} from '../utils/analytics/posthog/utils';

import {
  type LexContextLexFragment,
  useLexContextLexListQuery,
} from './__generated-gql-types__/LexContext.generated';

// list of Lex keys to evaluate. Please actively remove the key if it's been cleaned up completely
// For posthog feature flags, use the `POSTHOG_AB_PREFIX` prefix like this: `TEST_FLAG = `${POSTHOG_AB_PREFIX}test-flag`,
export enum LexSlug {
  RAMP_AI_GENERATOR = 'ramp-ai-generator',
  FEATURE_APPLICANT_USE_CREDITS = 'feature-applicant-use-credits',
  FEATURE_INBOX_DETAILS_SIDEBAR = 'feature-inbox-details-sidebar',
  FEATURE_APPLICANT_FREE_MONTH = 'feature-applicant-free-month',
  FEATURE_CANCEL_PAUSE_SUBSCRIPTION = 'feature-cancel-pause-subscription',
  LINKEDIN_UPSELL = 'linkedin-upsell',
  FEATURE_MANAGE_SUBSCRIPTION = 'feature-manage-subscription',
  FEATURE_COACHING_REQUEST = 'feature-coaching-request',
  UPGRADE_PACKAGE_FLOW = 'upgrade-package-flow',
  FEATURE_CUSTOMER_REFERRAL = 'feature-customer-referral',
  FEATURE_MULTI_EVENT_REGISTRATION = 'feature-multi-event-registration',
  INTRO_CALL_SHOW_INTEREST_SELECTION = 'intro-call-show-interest-selection',
  GUEST_CHECKOUT = 'guest-checkout',
  RECURRING_SESSIONS = 'recurring-sessions',
  LELAND_PLUS_CHECKOUT_ON_STACK = 'leland-plus-checkout-on-stack',
  BOOTCAMP_REVAMP = 'bootcamp-revamp',
  GUEST_EVENT_SIGNUP_AB_ENABLED = `${POSTHOG_AB_PREFIX}guest-event-signup-enabled`,
  REDO_COACH_CARDS = 'redo-coach-cards',
  BOOTCAMP_SRP = 'bootcamp-srp',
  SAVING_COACHES_AB_ENABLED = `${POSTHOG_AB_PREFIX}saving-coaches`,
  RAMP_COACH_SET_PRICE = 'ramp-coach-set-price',
  UPDATE_VOUCH_FLOW = 'update-vouch-flow',
}

// list of Lex slugs to evaluate
const LEX_SLUGS_TO_EVALUATE: LexSlug[] = Object.values(LexSlug).filter(
  (lexKey: LexSlug) => !isPostHogSlug(lexKey),
);

const MISSING_LEX_CONTEXT_PROVIDER =
  'You forgot to wrap your app in <LexProvider>';

interface LexContext {
  isLexEnabled: (lexKey: LexSlug) => boolean;
  isLexLoading: boolean;
}

export const LexContext = createContext<LexContext>({
  get isLexEnabled(): never {
    throw new Error(MISSING_LEX_CONTEXT_PROVIDER);
  },
  get isLexLoading(): never {
    throw new Error(MISSING_LEX_CONTEXT_PROVIDER);
  },
});

// to prevent potential layout shift, initialize some fully ramped lex slugs by default
// NOTE: should only be temporary until the Lex is removed
const DEFAULT_INIT_LIST: LexContextLexFragment[] = [];

/**
 * LeX is short for Leland Experiment to enable A/B testing.
 * `useLex` to check whether the experiment/feature is ramped to the current user/guest
 */
export const useLex: () => LexContext = () =>
  useContext<LexContext>(LexContext);

export const LexContextProvider: FC<PropsWithChildren> = ({ children }) => {
  // TODO: listen to user data change to reload rampedExperiments
  const { data, loading } = useLexContextLexListQuery({
    variables: { slugs: LEX_SLUGS_TO_EVALUATE },
    skip: LEX_SLUGS_TO_EVALUATE.length === 0,
  });

  const value: LexContext = useMemo(() => {
    const lexIsRampedMap = (data?.lexListBySlugs ?? DEFAULT_INIT_LIST).reduce<
      Record<string, boolean>
    >((map, lex) => {
      map[lex.slug] = lex.isRamped;
      return map;
    }, {});

    return {
      isLexEnabled: (slug: LexSlug) => {
        if (isPostHogSlug(slug)) {
          return phog.isFeatureEnabled(slug);
        }
        return !!lexIsRampedMap[slug];
      },
      isLexLoading: loading,
    };
  }, [data?.lexListBySlugs, loading]);

  return <LexContext.Provider value={value}>{children}</LexContext.Provider>;
};
